import MarkdownIt from 'markdown-it'
import DOMPurify from 'dompurify'

// 配置 markdown-it
const md = new MarkdownIt({
  html: true,        // 允许 HTML 标签
  linkify: true,     // 自动转换 URL 为链接
  typographer: true, // 启用一些语言中性的替换 + 引号美化
  breaks: true,      // 转换 '\n' 为 <br>
  tables: true       // GFM 表格支持（内置）
})

// 自定义渲染规则：处理数学公式代码块
const defaultRender = md.renderer.rules.fence || function(tokens, idx, options, env, self) {
  return self.renderToken(tokens, idx, options)
}

md.renderer.rules.fence = function(tokens, idx, options, env, self) {
  const token = tokens[idx]
  const info = token.info ? token.info.trim() : ''
  
  // 如果是数学公式代码块
  if (info === 'math' || info === 'latex') {
    const content = token.content.trim()
    return `<div class="math-block">$$${content}$$</div>`
  }
  
  return defaultRender(tokens, idx, options, env, self)
}

// 处理行内数学公式 \(...\) 和 $...$
// 使用更简单的方法：在解析后通过正则表达式替换
// 这样可以避免与 markdown-it 的内部规则冲突

// 处理块级数学公式 $$...$$
md.block.ruler.before('fence', 'math_block', function(state, start, end, silent) {
  const beginMarker = '$$'
  const endMarker = '$$'
  
  let pos = state.bMarks[start] + state.tShift[start]
  let max = state.eMarks[start]
  
  if (pos + beginMarker.length > max) return false
  
  const marker = state.src.slice(pos, pos + beginMarker.length)
  if (marker !== beginMarker) return false
  
  // 查找结束标记
  let nextLine = start
  let found = false
  
  while (nextLine < end) {
    nextLine++
    if (nextLine >= end) break
    
    pos = state.bMarks[nextLine] + state.tShift[nextLine]
    max = state.eMarks[nextLine]
    
    if (pos < max && state.src.slice(pos, pos + endMarker.length) === endMarker) {
      found = true
      break
    }
  }
  
  if (!found) return false
  
  if (!silent) {
    const token = state.push('math_block', 'math', 0)
    token.content = state.getLines(start, nextLine, state.blkIndent, false)
      .replace(/^\$\$/, '')
      .replace(/\$\$$/, '')
      .trim()
    token.map = [start, nextLine + 1]
    token.markup = beginMarker
  }
  
  state.line = nextLine + 1
  return true
})

// 自定义数学公式渲染
md.renderer.rules.math_inline = function(tokens, idx) {
  const token = tokens[idx]
  const content = token.content.trim()
  // 如果内容为空，不渲染
  if (!content) return ''
  return `<span class="math-inline">\\(${content}\\)</span>`
}

md.renderer.rules.math_block = function(tokens, idx) {
  const token = tokens[idx]
  const content = token.content.trim()
  // 如果内容为空，不渲染
  if (!content) return ''
  return `<div class="math-block">$$${content}$$</div>`
}

/**
 * 将 Markdown 转换为安全的 HTML
 * @param {string} markdown - Markdown 文本
 * @returns {string} - 安全的 HTML 字符串
 */
export function markdownToHtml(markdown) {
  if (!markdown || typeof markdown !== 'string') {
    return ''
  }
  
  try {
    // 先保护行内公式，避免被 markdown-it 处理
    const placeholders = []
    let placeholderIndex = 0
    
    // 保护 \(...\) 格式的行内公式
    let protectedMarkdown = markdown
    let searchPos = 0
    
    while (true) {
      const startPos = protectedMarkdown.indexOf('\\(', searchPos)
      if (startPos === -1) break
      
      const endPos = protectedMarkdown.indexOf('\\)', startPos + 2)
      if (endPos === -1) {
        searchPos = startPos + 2
        continue
      }
      
      const content = protectedMarkdown.slice(startPos + 2, endPos)
      // 使用 HTML 注释作为占位符，markdown-it 不会处理它
      const placeholder = `<!--MATH_INLINE_${placeholderIndex}-->`
      placeholders.push({ type: 'inline', content, placeholder })
      
      protectedMarkdown = protectedMarkdown.slice(0, startPos) + 
                         placeholder + 
                         protectedMarkdown.slice(endPos + 2)
      searchPos = startPos + placeholder.length
      placeholderIndex++
    }
    
    // 保护 $...$ 格式的行内公式（但不是 $$...$$）
    searchPos = 0
    while (true) {
      // 查找单独的 $（前后都不是 $）
      const dollarPos = protectedMarkdown.indexOf('$', searchPos)
      if (dollarPos === -1) break
      
      const prevChar = dollarPos > 0 ? protectedMarkdown[dollarPos - 1] : ' '
      const nextChar = dollarPos + 1 < protectedMarkdown.length ? protectedMarkdown[dollarPos + 1] : ' '
      
      // 如果前后都是 $，跳过（这是块级公式）
      if (prevChar === '$' || nextChar === '$') {
        searchPos = dollarPos + 1
        continue
      }
      
      // 查找结束的 $
      const endDollarPos = protectedMarkdown.indexOf('$', dollarPos + 1)
      if (endDollarPos === -1) break
      
      // 确保结束的 $ 后面不是 $
      if (endDollarPos + 1 < protectedMarkdown.length && 
          protectedMarkdown[endDollarPos + 1] === '$') {
        searchPos = endDollarPos + 1
        continue
      }
      
      const content = protectedMarkdown.slice(dollarPos + 1, endDollarPos)
      // 使用 HTML 注释作为占位符，markdown-it 不会处理它
      const placeholder = `<!--MATH_INLINE_${placeholderIndex}-->`
      placeholders.push({ type: 'inline', content, placeholder })
      
      protectedMarkdown = protectedMarkdown.slice(0, dollarPos) + 
                         placeholder + 
                         protectedMarkdown.slice(endDollarPos + 1)
      searchPos = dollarPos + placeholder.length
      placeholderIndex++
    }
    
    // 使用 markdown-it 解析
    let html = md.render(protectedMarkdown)
    
    // 恢复行内公式（在 DOMPurify 之前）
    placeholders.forEach(({ type, content, placeholder }) => {
      if (type === 'inline') {
        // HTML 注释格式，直接使用字符串替换（更可靠）
        // 公式内容保持原样，让 MathJax 处理
        // 直接字符串替换，不使用正则表达式
        html = html.split(placeholder).join(`<span class="math-inline">\\(${content}\\)</span>`)
      }
    })
    
    // 使用 DOMPurify 清理 HTML，防止 XSS
    // 注意：HTML 注释会被 DOMPurify 保留，所以占位符替换必须在 DOMPurify 之前完成
    const cleanHtml = DOMPurify.sanitize(html, {
      ADD_TAGS: ['math', 'mi', 'mo', 'mn', 'mfrac', 'msup', 'msub', 'munderover'],
      ADD_ATTR: ['class', 'id'],
      KEEP_CONTENT: true
    })
    
    return cleanHtml
  } catch (error) {
    console.error('Markdown parsing error:', error)
    // 降级方案：返回转义的原始文本
    return DOMPurify.sanitize(markdown.replace(/</g, '&lt;').replace(/>/g, '&gt;'))
  }
}

/**
 * 批量处理多个 Markdown 文档
 * @param {Array<string>} markdowns - Markdown 文本数组
 * @returns {Array<string>} - HTML 字符串数组
 */
export function batchMarkdownToHtml(markdowns) {
  return markdowns.map(md => markdownToHtml(md))
}

export default {
  markdownToHtml,
  batchMarkdownToHtml
}

